/**
 * ItemKit shared internals
 *
 * Types, rarity helpers, slot dimensions, and the exact-fidelity style
 * literals that couple the ItemKit components. Pure-move out of ItemKit.tsx;
 * no logic changes.
 */

import { RARITY, RARITY_GLOW } from '../../theme';

// ============================================================================
// Types
// ============================================================================

export interface ItemData {
  id: string;
  name: string;
  icon?: string;
  description?: string;
  rarity?: ItemRarity;
  quantity?: number;
  level?: number;
  type?: string;
  stats?: Record<string, number>;
}

export type ItemRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

export type ItemSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

// ============================================================================
// Theme Colors
// ============================================================================

// Rarity colors + glows now come from the canonical theme palette (RARITY /
// RARITY_GLOW) — WoW-classic hues, shared with Tooltip + ShopView.
export const RARITY_COLORS = RARITY;
export const RARITY_GLOWS = RARITY_GLOW;

export const SIZE_DIMENSIONS: Record<ItemSize, { width: number; height: number }> = {
  xs: { width: 24, height: 24 },
  sm: { width: 32, height: 32 },
  md: { width: 48, height: 48 },
  lg: { width: 64, height: 64 },
  xl: { width: 80, height: 80 },
};

// Exact-fidelity literals carried over from the prior styled
// definition — the brand-orange selection hue + the white item-name text
// have no 1:1 theme token, so keeping the literals makes the de-style pass a
// visual no-op.
export const SELECT_BORDER = '#ff6b00';
export const SLOT_ITEM_BG = 'rgba(255, 255, 255, 0.08)';
export const SLOT_EMPTY_BG = 'rgba(0, 0, 0, 0.3)';
export const SLOT_ITEM_BORDER = 'rgba(255, 255, 255, 0.15)';
export const SLOT_EMPTY_BORDER = 'rgba(255, 255, 255, 0.05)';
export const SLOT_HOVER_BG = 'rgba(255, 255, 255, 0.12)';
export const SLOT_INSET_SHADOW = 'inset 0 2px 4px rgba(0,0,0,0.5)';
export const SLOT_HOVER_SHADOW_BASE = '0 4px 12px rgba(0, 0, 0, 0.3)';
export const SLOT_HOVER_GLOW_FALLBACK = '0 0 10px rgba(255, 107, 0, 0.2)';
export const ROW_SELECTED_BG = 'rgba(255, 107, 0, 0.15)';
export const ROW_SELECTED_BORDER = 'rgba(255, 107, 0, 0.5)';
export const ROW_HOVER_BG = 'rgba(255, 255, 255, 0.08)';
export const MUTED_TEXT = '#9ca3af';
export const ACCENT = '#fbbf24';

export function rarityColor(rarity?: string): string | undefined {
  if (rarity && RARITY_COLORS[rarity as keyof typeof RARITY_COLORS]) {
    return RARITY_COLORS[rarity as keyof typeof RARITY_COLORS];
  }
  return undefined;
}

export function rarityGlow(rarity?: string): string | undefined {
  if (rarity && RARITY_GLOWS[rarity as keyof typeof RARITY_GLOWS]) {
    return RARITY_GLOWS[rarity as keyof typeof RARITY_GLOWS];
  }
  return undefined;
}
